import { useState, useEffect, useMemo, useCallback } from 'react';
import { deviceTypesApi } from '../../../services/apiClient';
import LocationService from '../../../services/locationService';
import { getLocationId } from '../utils/locationTransformers';

/**
 * Hook for managing expected device types logic
 * Extracts ~100 lines of expected device type logic from the monolith
 */
export const useExpectedDevices = ({ selectedLocation, setSelectedLocation, devices, setLocations }) => {
  const [allDeviceTypes, setAllDeviceTypes] = useState([]);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
  const [expectedDevicesExpanded, setExpectedDevicesExpanded] = useState(true);
  const [savingExpectedDevices, setSavingExpectedDevices] = useState(false);

  // Load device types once on mount
  useEffect(() => {
    const loadDeviceTypes = async () => {
      setLoadingDeviceTypes(true);
      try {
        const response = await deviceTypesApi.getAll({ limit: 100 });
        const types = response?.data?.data || response?.data || [];
        setAllDeviceTypes(Array.isArray(types) ? types : []);
      } catch (error) {
        console.error('Error loading device types:', error);
        setAllDeviceTypes([]);
      } finally {
        setLoadingDeviceTypes(false);
      }
    };
    loadDeviceTypes();
  }, []);

  // Get expected device types from location data
  const expectedDeviceTypes = useMemo(() => {
    if (!selectedLocation?.data || !Array.isArray(selectedLocation.data)) return [];
    const entry = selectedLocation.data.find(d => d.name === 'requiredDeviceTypes');
    if (!entry?.value) return [];

    return entry.value.map(item => {
      if (typeof item === 'number') {
        return { deviceTypeId: item, minCount: 1 };
      }
      return item;
    });
  }, [selectedLocation?.data]);

  // Compute expected devices status (fulfilled vs missing)
  const expectedDevicesStatus = useMemo(() => {
    if (!expectedDeviceTypes.length) return [];

    return expectedDeviceTypes.map(expected => {
      const deviceType = allDeviceTypes.find(dt => dt.id === expected.deviceTypeId);
      const devicesOfType = devices.filter(d =>
        (d.deviceTypeModel?.id || d.deviceType) === expected.deviceTypeId
      );
      const actualCount = devicesOfType.length;
      const minCount = expected.minCount || 1;

      return {
        id: expected.deviceTypeId,
        name: deviceType?.FrontEndName || deviceType?.friendlyName || `Type ${expected.deviceTypeId}`,
        minCount,
        actualCount,
        fulfilled: actualCount >= minCount,
        devices: devicesOfType
      };
    });
  }, [expectedDeviceTypes, devices, allDeviceTypes]);

  // Save expected device types to location data
  const saveExpectedDeviceTypes = useCallback(async (newExpectedTypes) => {
    if (!selectedLocation) return;

    setSavingExpectedDevices(true);
    try {
      const locationId = getLocationId(selectedLocation);
      let existingData = Array.isArray(selectedLocation.data) ? [...selectedLocation.data] : [];
      existingData = existingData.filter(d => d.name !== 'requiredDeviceTypes');

      if (newExpectedTypes.length > 0) {
        existingData.push({
          name: 'requiredDeviceTypes',
          type: 'deviceTypeArray',
          value: newExpectedTypes,
          visible: true,
          allowedUsers: [],
          sendWithAlarm: false,
          permissionLevel: 'admin'
        });
      }

      await LocationService.updateLocation(locationId, { data: existingData });

      const updatedLocation = { ...selectedLocation, data: existingData };
      setSelectedLocation(updatedLocation);

      if (setLocations) {
        setLocations((prevLocations) => {
          const updateNode = (nodes) => {
            return nodes.map((node) => {
              if (getLocationId(node) === locationId) {
                return { ...node, data: existingData };
              }
              if (node.children) {
                return { ...node, children: updateNode(node.children) };
              }
              return node;
            });
          };
          return updateNode(prevLocations);
        });
      }
    } catch (error) {
      console.error('Error saving expected device types:', error);
    } finally {
      setSavingExpectedDevices(false);
    }
  }, [selectedLocation, setSelectedLocation, setLocations]);

  const handleAddExpectedDeviceType = useCallback((deviceType) => {
    if (!deviceType || expectedDeviceTypes.some(e => e.deviceTypeId === deviceType.id)) return;
    saveExpectedDeviceTypes([...expectedDeviceTypes, { deviceTypeId: deviceType.id, minCount: 1 }]);
  }, [expectedDeviceTypes, saveExpectedDeviceTypes]);

  const handleRemoveExpectedDeviceType = useCallback((typeId) => {
    saveExpectedDeviceTypes(expectedDeviceTypes.filter(e => e.deviceTypeId !== typeId));
  }, [expectedDeviceTypes, saveExpectedDeviceTypes]);

  const handleUpdateMinCount = useCallback((typeId, newMinCount) => {
    const count = Math.max(1, parseInt(newMinCount) || 1);
    saveExpectedDeviceTypes(
      expectedDeviceTypes.map(e =>
        e.deviceTypeId === typeId ? { ...e, minCount: count } : e
      )
    );
  }, [expectedDeviceTypes, saveExpectedDeviceTypes]);

  return {
    allDeviceTypes,
    loadingDeviceTypes,
    expectedDeviceTypes,
    expectedDevicesStatus,
    expectedDevicesExpanded,
    setExpectedDevicesExpanded,
    savingExpectedDevices,
    handleAddExpectedDeviceType,
    handleRemoveExpectedDeviceType,
    handleUpdateMinCount
  };
};

export default useExpectedDevices;
